/**
 * Shape of the system super-category endpoints.
 *
 * `GET/POST /system/category-variant/super-categories`,
 * `GET/PATCH/DELETE .../{id}` and `GET .../{id}/tree` all document their 200 with
 * a bare description and no response schema, so every field here is optional and
 * an index signature keeps extra keys from breaking consumers. The names are the
 * ones the existing screens already read off the payload — `name`, `slug`,
 * `description`, `image`, `status`, `parentId`, `priority`, `createdAt`, `id` —
 * plus `children` from the tree endpoint and the two spellings of the child count
 * that `CategoryCardData` already tolerates.
 *
 * Do NOT add a field here without seeing it in a real payload. The subscription
 * equivalent once carried `startDate`/`endDate`, which do not exist, and because
 * the fixtures used the same invented names the suite stayed green while two
 * columns rendered an em dash on every row.
 */
export interface SystemSuperCategory {
  id?: string;
  /** Some list payloads carry `_id` instead — `CategoryCardData` accepts both,
   *  so consumers resolving an id must too. */
  _id?: string;
  name?: string;
  slug?: string;
  description?: string;
  image?: string;
  /** `ACTIVE | INACTIVE` in every payload seen so far. Treated as an open string
   *  because the enum is not in the export. */
  status?: string;
  /** `null` on a root super-category. Absent is NOT the same fact — see the
   *  detail page, which must not report an unresolved parent as "root". */
  parentId?: string | null;
  priority?: number;
  totalSubCategories?: number;
  /** The lower-case spelling some payloads use. */
  totalSubcategories?: number;
  /** Populated by the `/{id}/tree` endpoint. */
  children?: SystemSuperCategory[];
  createdAt?: string;
  updatedAt?: string;

  [key: string]: unknown;
}

/**
 * Pull the super-category out of whatever the endpoint wrapped it in.
 *
 * No response schema is documented for any of these paths, so a payload may
 * arrive bare or inside this app's usual `{ data }` envelope. Both the detail and
 * the tree query go through here so they cannot disagree about it.
 *
 * A `data` key means this IS the envelope — no super-category field is named
 * `data` — so an envelope carrying `data: null` reports "no record" rather than
 * falling through and caching the wrapper as if it were one.
 */
export function unwrapSuperCategory(res: unknown): SystemSuperCategory | undefined {
  if (!res || typeof res !== "object" || Array.isArray(res)) return undefined;

  if ("data" in (res as Record<string, unknown>)) {
    const inner = (res as { data?: unknown }).data;
    return inner && typeof inner === "object" && !Array.isArray(inner)
      ? (inner as SystemSuperCategory)
      : undefined;
  }

  return res as SystemSuperCategory;
}

/**
 * The direct children of a super-category, from the `/{id}/tree` response.
 *
 * The endpoint documents no schema, and "tree" could reasonably mean either the
 * requested node WITH its children or the children alone — so both are accepted.
 * Only direct children are returned: this feeds a detail page's child list, and
 * flattening grandchildren into it would show a category under a parent it does
 * not belong to.
 */
export function childrenOfTree(res: unknown): SystemSuperCategory[] {
  if (Array.isArray(res)) return res as SystemSuperCategory[];

  const node = unwrapSuperCategory(res);
  if (!node) return [];
  if (Array.isArray(node.children)) return node.children;

  // An envelope whose `data` is itself the array of children.
  const inner = (res as { data?: unknown })?.data;
  return Array.isArray(inner) ? (inner as SystemSuperCategory[]) : [];
}
